import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DocumentTypeEnum } from '../../_enums/document-type.enum';
import { DocumentService } from '../../_services/document.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarData } from '../../_models/snackbar-data.model';
import { CustomSnackbar } from '../custom-snackbar/custom-snackbar';
import { SnackbarType } from '../../_enums/snackbar-type.enum';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-upload-document',
  imports: [MatIcon, MatButtonModule, TranslateModule, MatTooltipModule],
  templateUrl: './upload-document.html',
  styleUrl: './upload-document.scss',
})
export class UploadDocument implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('optionalFileInput') optionalFileInput!: ElementRef<HTMLInputElement>;
  @Input() allowOptionalFiles = true;
  @Input() optionalFilesTitle!: string;
  @Input({ required: true }) documentType!: DocumentTypeEnum;
  @Output() download = new EventEmitter<void>();

  public readonly maxOptionalDocuments = 5;
  public document: File | undefined;
  public optionalDocuments: File[] = [];
  public isNameEllipsisActive = false;
  public optionalEllipsisStates: boolean[] = [];

  private documentService = inject(DocumentService);
  private readonly translateService = inject(TranslateService);
  private readonly snackBar = inject(MatSnackBar);

  private readonly allowedRequiredTypes = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  private readonly allowedOptionalTypes = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
  ];

  private readonly maxFileSizeBytes = 10 * 1024 * 1024; // 10MB

  private isValidFileType(file: File, allowedTypes: string[]): boolean {
    return allowedTypes.includes(file.type);
  }

  private isDuplicateFile(file: File, existingFiles: File[]): boolean {
    return existingFiles.some((f) => f.name === file.name);
  }

  private isValidFileSize(file: File): boolean {
    return file.size <= this.maxFileSizeBytes;
  }

  public getDocumentFormat(file: File): string {
    switch (file.type) {
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      case 'application/docx':
        return 'DOCX';
      case 'application/msword':
        return 'DOC';
      case 'application/pdf':
        return 'PDF';
      default:
        return 'DOCX';
    }
  }

  public get requiredFileTitle(): string {
    switch (this.documentType) {
      case DocumentTypeEnum.DECISION:
        return 'transcriber.decisionDocument';
      case DocumentTypeEnum.ACTION_PLAN:
        return 'transcriber.actionPlanDocument';
      case DocumentTypeEnum.TRANSCRIPT:
        return 'transcriber.transcriptDocument';
      default:
        return '';
    }
  }

  public get documentFormat(): string {
    if (!this.document) return '';
    return this.getDocumentFormat(this.document);
  }

  public ngOnInit(): void {
    this.documentService.getDocument(this.documentType).subscribe((file) => {
      this.document = file;
    });

    this.documentService.getOptionalDocuments().subscribe((files) => {
      this.optionalDocuments = files;
    });
  }
  public ngOnDestroy(): void {
    if (this.allowOptionalFiles) {
      this.documentService.clearOptionalDocuments();
    }
  }

  public checkEllipsis(el: HTMLElement, index?: number): void {
    const active = el.scrollWidth > el.clientWidth;
    if (index !== undefined) {
      this.optionalEllipsisStates[index] = active;
    } else {
      this.isNameEllipsisActive = active;
    }
  }

  public isEllipsisActive(el: HTMLElement): boolean {
    return el.scrollWidth > el.clientWidth;
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    if (!this.isValidFileType(file, this.allowedRequiredTypes)) {
      this.showErrorToast('upload.invalidTranscriptFileType');
      input.value = '';
      return;
    }

    if (!this.isValidFileSize(file)) {
      this.showErrorToast('upload.fileTooLarge');
      input.value = '';
      return;
    }

    if (this.isDuplicateFile(file, this.document ? [this.document] : [])) {
      input.value = '';
      return;
    }

    if (!this.documentService.getDocumentSnapshot(this.documentType)) {
      this.documentService.setDocument(this.documentType, file);
    } else {
      this.documentService.replaceDocument(this.documentType, file);
    }

    input.value = '';
  }

  public onOptionalFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) return;

    const allFiles = Array.from(input.files);

    const validFiles = allFiles.filter((file) =>
      this.isValidFileType(file, this.allowedOptionalTypes),
    );

    if (validFiles.length < allFiles.length) {
      this.showErrorToast('upload.invalidOptionalFileType');
    }

    const sizeValidFiles = validFiles.filter((file) => this.isValidFileSize(file));

    if (sizeValidFiles.length < validFiles.length) {
      this.showErrorToast('upload.fileTooLarge');
    }

    const uniqueFiles = sizeValidFiles.filter(
      (file) => !this.isDuplicateFile(file, this.optionalDocuments),
    );

    if (uniqueFiles.length === 0) {
      input.value = '';
      return;
    }

    const remainingSlots = this.maxOptionalDocuments - this.optionalDocuments.length;

    if (remainingSlots <= 0) {
      input.value = '';
      return;
    }

    const filesToAdd = uniqueFiles.slice(0, remainingSlots);

    filesToAdd.forEach((file) => {
      this.documentService.addOptionalDocument(file);
    });

    if (uniqueFiles.length > remainingSlots) {
      this.showErrorToast('upload.maxOptionalDocuments');
    }

    input.value = '';
  }

  public onUploadClick(): void {
    this.fileInput.nativeElement.click();
  }

  public onDeleteClick(): void {
    this.documentService.deleteDocument(this.documentType);
  }

  public onDeleteOptionalClick(index: number): void {
    this.documentService.removeOptionalDocument(index);
  }

  public onDownloadClick(): void {
    if (!this.document) {
      return;
    }
    this.downloadFile(this.document, this.document.name);
  }

  public onDownloadOptionalClick(index: number): void {
    const file = this.optionalDocuments[index];
    if (!file) {
      return;
    }
    this.downloadFile(file, file.name);
  }

  public onAddOptionalClick(): void {
    this.optionalFileInput.nativeElement.click();
  }

  private downloadFile(file: File, filename: string): void {
    const url = window.URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private showErrorToast(translationKey: string): void {
    this.translateService.get(translationKey).subscribe((message: string) => {
      this.snackBar.openFromComponent(CustomSnackbar, {
        duration: 5000,
        data: new SnackbarData(message, SnackbarType.WARNING),
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
      });
    });
  }
}
